import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import Button from '../Button'
import DateInput from '../DateInput'
import PresetButton from './PresetButton'
import { formatDate, formatDateParam, todayIso } from '../../../utils/dateHelpers'

const TRIGGER_CLASSES = 'tw:bg-white tw:border tw:border-solid tw:border-gray-300 '
  + 'tw:rounded! tw:inline-flex tw:items-center tw:justify-center tw:gap-2 '
  + 'tw:px-3 tw:py-2 tw:text-sm tw:text-gray-500 '
  + 'tw:focus:outline-none tw:focus:border-ds-primary!'

const POPOVER_CLASSES = 'tw:ds-card tw:shadow-sm tw:absolute tw:right-0 tw:top-full tw:mt-2 '
  + 'tw:w-96 tw:z-popover tw:px-4 tw:py-6'

const TITLE_CLASSES = 'tw:m-0! tw:text-ds-neutral-highest tw:text-md tw:font-medium'

const LABEL_CLASSES = 'tw:text-gray-500 tw:text-md tw:font-medium'

const noop = () => null

const formatRange = (from, to) => from && to ? `${formatDate(from)} - ${formatDate(to)}` : ''

const shiftDaysIso = days => {
  const date = new Date()
  date.setDate(date.getDate() - days)
  return formatDateParam(date)
}

const shiftMonthsIso = months => {
  const date = new Date()
  date.setMonth(date.getMonth() - months)
  return formatDateParam(date)
}

const buildDefaultPresets = minDate => [
  { label: 'Les 30 derniers jours', getRange: () => ({ from: shiftDaysIso(30), to: todayIso() }) },
  { label: 'Les 3 derniers mois', getRange: () => ({ from: shiftMonthsIso(3), to: todayIso() }) },
  { label: 'Les 6 derniers mois', getRange: () => ({ from: shiftMonthsIso(6), to: todayIso() }) },
  { label: 'Les 12 derniers mois', getRange: () => ({ from: shiftMonthsIso(12), to: todayIso() }) },
  { label: 'Tout voir', getRange: () => ({ from: minDate, to: todayIso() }) }
]

const DateRangePicker = ({
  endDate = null,
  maxDate = null,
  minDate = null,
  onChange,
  showPresets = false,
  startDate = null
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [pendingFrom, setPendingFrom] = useState(startDate)
  const [pendingTo, setPendingTo] = useState(endDate)
  const containerRef = useRef(null)
  const presets = useMemo(() => showPresets ? buildDefaultPresets(minDate) : [], [showPresets, minDate])

  const toggle = useCallback(() => setIsOpen(prev => !prev), [])

  useEffect(() => {
    if (isOpen)
      return
    setPendingFrom(startDate)
    setPendingTo(endDate)
  }, [startDate, endDate, isOpen])

  useEffect(() => {
    if (!isOpen)
      return noop
    const handleClickOutside = event => {
      if (event.target.closest('.flatpickr-calendar'))
        return
      if (containerRef.current && !containerRef.current.contains(event.target))
        setIsOpen(false)
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isOpen])

  const handleCancel = useCallback(() => {
    setPendingFrom(startDate)
    setPendingTo(endDate)
    setIsOpen(false)
  }, [startDate, endDate])

  const canSave = Boolean(pendingFrom && pendingTo && pendingFrom <= pendingTo)

  const handleSave = useCallback(() => {
    if (!canSave)
      return
    onChange({ from: pendingFrom, to: pendingTo })
    setIsOpen(false)
  }, [canSave, pendingFrom, pendingTo, onChange])

  const handlePresetSelect = useCallback(preset => {
    onChange(preset.getRange())
    setIsOpen(false)
  }, [onChange])

  return (
    <div ref={containerRef} className="tw:relative tw:inline-block">
      <button className={TRIGGER_CLASSES} type="button" onClick={toggle}>
        <i className="fas fa-calendar-alt" />
        <span>{formatRange(startDate, endDate)}</span>
        <i className={`fas fa-chevron-${isOpen ? 'up' : 'down'} tw:text-2xs`} />
      </button>
      {isOpen ? (
        <div className={POPOVER_CLASSES}>
          {presets.length > 0 ? (
            <>
              <div className="tw:flex tw:flex-col tw:gap-1">
                {presets.map(preset =>
                  <PresetButton key={preset.label} preset={preset} onSelect={handlePresetSelect} />
                )}
              </div>
              <hr className="tw:m-0!" />
            </>
          ) : null}
          <p className={TITLE_CLASSES}>Période personnalisée :</p>
          <div className="tw:flex tw:items-center tw:gap-2">
            <span className={LABEL_CLASSES}>Du</span>
            <DateInput
              clearable={false}
              maxDate={pendingTo || maxDate}
              minDate={minDate}
              placeholder="Date de départ"
              value={pendingFrom}
              onChange={setPendingFrom}
            />
            <span className={LABEL_CLASSES}>au</span>
            <DateInput
              clearable={false}
              maxDate={maxDate}
              minDate={pendingFrom || minDate}
              placeholder="Date de fin"
              value={pendingTo}
              onChange={setPendingTo}
            />
          </div>
          <hr className="tw:m-0!" />
          <div className="tw:flex tw:gap-2">
            <Button className="tw:grow" variant="outline" onClick={handleCancel}>
              Annuler
            </Button>
            <Button className="tw:grow" disabled={!canSave} variant="primary" onClick={handleSave}>
              Enregistrer
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  )
}

DateRangePicker.propTypes = {
  endDate: PropTypes.string,
  maxDate: PropTypes.string,
  minDate: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  showPresets: PropTypes.bool,
  startDate: PropTypes.string
}

DateRangePicker.defaultProps = {
  endDate: null,
  maxDate: null,
  minDate: null,
  showPresets: true,
  startDate: null
}

export default DateRangePicker
